import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

const STATUS = {
  READY: { label: "Ready", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", Icon: CheckCircle2 },
  WARNING: { label: "Warning", cls: "bg-amber-100 text-amber-700 border-amber-300", Icon: AlertTriangle },
  BLOCKED: { label: "Blocked", cls: "bg-red-100 text-red-700 border-red-300", Icon: XCircle },
};

function Pill({ status, testid }) {
  const s = STATUS[status] || STATUS.READY;
  const Icon = s.Icon;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${s.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {s.label}
    </span>
  );
}

function ItemList({ items, tone }) {
  const dot = tone === "blocker" ? "bg-red-500" : "bg-amber-500";
  return (
    <ul className="space-y-1.5">
      {items.map((it, i) => (
        <li key={i} data-testid={`readiness-item-${it.code}`} className="flex items-start gap-2 text-sm">
          <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${dot}`} />
          <span><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mr-1.5">{it.section || ""}</span>{it.detail}</span>
        </li>
      ))}
    </ul>
  );
}

// Employee readiness panel — display-only. `readiness` is the authoritative backend object.
export function EmployeeReadinessPanel({ readiness, action }) {
  if (!readiness) return null;
  const { status, blocker_count = 0, warning_count = 0, blockers = [], warnings = [] } = readiness;
  return (
    <div data-testid="employee-readiness-panel" className="border border-border bg-neutral-50 p-4 mb-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
          <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Payroll Readiness</span>
          <Pill status={status} testid="employee-readiness-status" />
        </div>
        <span className="text-[11px] font-mono tabular-nums text-muted-foreground" data-testid="employee-readiness-counts">
          {blocker_count} blocker{blocker_count === 1 ? "" : "s"} · {warning_count} warning{warning_count === 1 ? "" : "s"}
        </span>
      </div>
      {status === "READY" ? (
        <p className="text-sm text-emerald-700">This employee is ready for payroll.</p>
      ) : (
        <div className="space-y-3">
          {blockers.length > 0 && <div><div className="text-[10px] font-mono uppercase tracking-widest text-red-600 mb-1.5">Blockers</div><ItemList items={blockers} tone="blocker" /></div>}
          {warnings.length > 0 && <div><div className="text-[10px] font-mono uppercase tracking-widest text-amber-600 mb-1.5">Warnings</div><ItemList items={warnings} tone="warning" /></div>}
        </div>
      )}
      {action && (
        <button data-testid="employee-readiness-action" onClick={action.onClick} className="mt-3 inline-flex items-center gap-1.5 border border-black px-3 py-1.5 text-xs hover:bg-black hover:text-white transition-colors">
          {action.label}
        </button>
      )}
    </div>
  );
}

// Company payroll readiness summary — fetches its own authoritative status.
export function CompanyReadinessSummary({ companyId, onEdit }) {
  const [r, setR] = useState(null);
  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/readiness`).then((res) => setR(res.data)).catch(() => setR(null));
  }, [companyId]);
  if (!r) return null;
  const { status, blocker_count = 0, warning_count = 0, blockers = [], warnings = [] } = r;
  return (
    <div data-testid="company-readiness-summary" className="border border-border bg-white p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
          <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Company Payroll Readiness</span>
          <Pill status={status} testid="company-readiness-status" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-mono tabular-nums text-muted-foreground">{blocker_count} blocker{blocker_count === 1 ? "" : "s"} · {warning_count} warning{warning_count === 1 ? "" : "s"}</span>
          {onEdit && <button data-testid="company-readiness-edit" onClick={onEdit} className="text-xs border border-current text-muted-foreground hover:text-black hover:border-black px-2.5 py-1 transition-colors">Company settings</button>}
        </div>
      </div>
      {status === "READY" ? (
        <p className="text-sm text-emerald-700">Company configuration is ready for payroll.</p>
      ) : (
        <div className="space-y-3 mt-2">
          {blockers.length > 0 && <div><div className="text-[10px] font-mono uppercase tracking-widest text-red-600 mb-1.5">Blockers</div><ItemList items={blockers} tone="blocker" /></div>}
          {warnings.length > 0 && <div><div className="text-[10px] font-mono uppercase tracking-widest text-amber-600 mb-1.5">Warnings</div><ItemList items={warnings} tone="warning" /></div>}
        </div>
      )}
    </div>
  );
}
